import { toPlainText } from './richText';
import type { FinderOpportunity } from './types';

/** 0 any, 1 ongoing, 2 one time (the stock widget's `frequency`) */
export type Frequency = 0 | 1 | 2;

/** What the visitor can change (and what the URL carries). */
export interface FilterState {
  campus: number | null;
  ministry: number | null;
  program: number | null;
  /** Opportunity type: in the church, in the community, around the world */
  type: number | null;
  frequency: Frequency;
  /** Required gender (Genders.Gender_ID); roles open to everyone always match */
  gender: number | null;
  /** The visitor's age; hides roles that need someone older */
  age: number | null;
  search: string;
}

/** What the page embeds bake in. Never shown as chips, never written to the URL. */
export interface EmbedOptions {
  includeMinistries: number[];
  excludeMinistries: number[];
  includePrograms: number[];
  excludePrograms: number[];
  /** Pin the list to specific opportunities (deep links) */
  ids: number[];
  exclude: number[];
  /** Stock `eventid`: opportunities attached to one event (Add_to_Event) */
  eventId: number | null;
  /** Stock `attributeids`: any of these attributes */
  attributeIds: number[];
  /** Cap the number of cards */
  limit: number | null;
  hideFilters: boolean;
  hideSearch: boolean;
  hideCampus: boolean;
}

export const EMPTY_FILTERS: FilterState = {
  campus: null,
  ministry: null,
  program: null,
  type: null,
  frequency: 0,
  gender: null,
  age: null,
  search: '',
};

export const EMPTY_EMBED: EmbedOptions = {
  includeMinistries: [],
  excludeMinistries: [],
  includePrograms: [],
  excludePrograms: [],
  ids: [],
  exclude: [],
  eventId: null,
  attributeIds: [],
  limit: null,
  hideFilters: false,
  hideSearch: false,
  hideCampus: false,
};

/** Number of filters the visitor has set, for the badge on the Filters button. */
export function countActive(f: FilterState): number {
  let n = 0;
  if (f.ministry !== null) n++;
  if (f.program !== null) n++;
  if (f.type !== null) n++;
  if (f.frequency !== 0) n++;
  if (f.gender !== null) n++;
  if (f.age !== null) n++;
  if (f.search.trim()) n++;
  return n;
}

// ---------------------------------------------------------------------------
// Params <-> state
//
// Short keys are what the widget writes to the URL. The stock
// <mpp-opportunity-finder> attribute names (congregationid, ministryid,
// programid, keyword, genderid, minimumage, frequency, eventid,
// attributeids) are read too, so an embed can be swapped attribute for
// attribute and old prefiltered links keep working.
// ---------------------------------------------------------------------------

const FILTER_KEYS: Record<string, keyof FilterState> = {
  campus: 'campus',
  congregationid: 'campus',
  congregation: 'campus',
  ministry: 'ministry',
  ministryid: 'ministry',
  program: 'program',
  programid: 'program',
  type: 'type',
  typeid: 'type',
  opportunitytype: 'type',
  opportunitytypeid: 'type',
  frequency: 'frequency',
  gender: 'gender',
  genderid: 'gender',
  age: 'age',
  minimumage: 'age',
  q: 'search',
  search: 'search',
  keyword: 'search',
};

const EMBED_KEYS: Record<string, keyof EmbedOptions> = {
  ministries: 'includeMinistries',
  includeministries: 'includeMinistries',
  excludeministries: 'excludeMinistries',
  programs: 'includePrograms',
  includeprograms: 'includePrograms',
  excludeprograms: 'excludePrograms',
  ids: 'ids',
  opportunities: 'ids',
  opportunityids: 'ids',
  exclude: 'exclude',
  excludeids: 'exclude',
  excludeopportunityids: 'exclude',
  event: 'eventId',
  eventid: 'eventId',
  attributes: 'attributeIds',
  attributeids: 'attributeIds',
  limit: 'limit',
  max: 'limit',
  hidefilters: 'hideFilters',
  hidesearch: 'hideSearch',
  hidecampus: 'hideCampus',
};

function norm(key: string): string {
  // "hide-filters", "hideFilters", "@MinistryID" -> "hidefilters", "ministryid"
  return key.trim().replace(/^@/, '').replace(/[-_]/g, '').toLowerCase();
}

function toInt(value: string): number | null {
  const n = Number(value);
  return Number.isFinite(n) ? Math.trunc(n) : null;
}

function toIntList(value: string): number[] {
  return value
    .split(',')
    .map((s) => toInt(s.trim()))
    .filter((n): n is number => n !== null);
}

function toBool(value: string): boolean {
  const v = value.trim().toLowerCase();
  return v === '1' || v === 'true' || v === 'yes' || v === 'on';
}

export function resolveFrequency(value: string): Frequency {
  const v = value.trim().toLowerCase();
  if (v === '1' || v === 'ongoing') return 1;
  if (v === '2' || v === 'onetime' || v === 'one-time' || v === 'one time' || v === 'dated')
    return 2;
  return 0;
}

/**
 * Apply raw key/value pairs (page URL, or the embed's data-params /
 * data-* attributes) on top of a filter state. Unknown keys are ignored.
 * Campus accepts a Congregation_ID, MP's Campus_Slug ("troy") or a name.
 */
export function applyRawParams(
  base: FilterState,
  entries: Iterable<[string, string]>,
  resolveCampus: (value: string) => number | null
): FilterState {
  const next: FilterState = { ...base };
  for (const [rawKey, rawValue] of entries) {
    const key = FILTER_KEYS[norm(rawKey)];
    const value = rawValue.trim();
    if (!key || !value) continue;
    switch (key) {
      case 'campus':
        next.campus = resolveCampus(value);
        break;
      case 'ministry':
        next.ministry = toInt(value);
        break;
      case 'program':
        next.program = toInt(value);
        break;
      case 'type':
        next.type = toInt(value);
        break;
      case 'frequency':
        next.frequency = resolveFrequency(value);
        break;
      case 'gender':
        next.gender = toInt(value);
        break;
      case 'age': {
        const n = toInt(value);
        next.age = n !== null && n > 0 ? n : null;
        break;
      }
      case 'search':
        next.search = value;
        break;
    }
  }
  return next;
}

/** The embed-level constraints from the same key/value pairs. */
export function parseEmbedOptions(entries: Iterable<[string, string]>): EmbedOptions {
  const out: EmbedOptions = { ...EMPTY_EMBED };
  for (const [rawKey, rawValue] of entries) {
    const key = EMBED_KEYS[norm(rawKey)];
    const value = rawValue.trim();
    if (!key || !value) continue;
    switch (key) {
      case 'includeMinistries':
      case 'excludeMinistries':
      case 'includePrograms':
      case 'excludePrograms':
      case 'ids':
      case 'exclude':
      case 'attributeIds':
        out[key] = toIntList(value);
        break;
      case 'eventId':
      case 'limit': {
        const n = toInt(value);
        out[key] = n !== null && n > 0 ? n : null;
        break;
      }
      case 'hideFilters':
      case 'hideSearch':
      case 'hideCampus':
        out[key] = toBool(value);
        break;
    }
  }
  return out;
}

export type SlugFor = (id: number) => string | null;

/**
 * Serialize the visitor's filters into short query params. The campus is
 * written as its slug ("troy") when one is known so links read well.
 */
export function filtersToParams(f: FilterState, slugFor?: SlugFor): URLSearchParams {
  const p = new URLSearchParams();
  if (f.campus !== null) p.set('campus', slugFor?.(f.campus) ?? String(f.campus));
  if (f.ministry !== null) p.set('ministry', String(f.ministry));
  if (f.program !== null) p.set('program', String(f.program));
  if (f.type !== null) p.set('type', String(f.type));
  if (f.frequency !== 0) p.set('frequency', f.frequency === 1 ? 'ongoing' : 'onetime');
  if (f.gender !== null) p.set('gender', String(f.gender));
  if (f.age !== null) p.set('age', String(f.age));
  if (f.search.trim()) p.set('q', f.search.trim());
  return p;
}

const MANAGED_KEYS = new Set(Object.keys(FILTER_KEYS));

/**
 * Rewrite the page URL so the current filters are shareable, leaving every
 * query param the widget doesn't own (WordPress previews, UTM tags) intact.
 */
export function syncFiltersToUrl(f: FilterState, slugFor?: SlugFor): void {
  if (typeof window === 'undefined') return;
  try {
    const url = new URL(window.location.href);
    for (const key of Array.from(url.searchParams.keys())) {
      if (MANAGED_KEYS.has(norm(key))) url.searchParams.delete(key);
    }
    for (const [k, v] of filtersToParams(f, slugFor)) url.searchParams.set(k, v);
    const next = url.toString();
    if (next !== window.location.href) window.history.replaceState(window.history.state, '', next);
  } catch {
    /* URL APIs unavailable, nothing to sync */
  }
}

// ---------------------------------------------------------------------------
// Matching
// ---------------------------------------------------------------------------

/** Church Wide opportunities belong to every campus, same as the stock widget. */
const CHURCH_WIDE = 1;

function normalize(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

export function haystack(o: FinderOpportunity): string {
  return normalize(
    [
      o.title,
      toPlainText(o.description),
      o.congregationName,
      o.locationName ?? '',
      o.programName,
      o.ministryName,
      o.type,
      o.roleTitle,
      o.contact?.name ?? '',
      ...o.attributes.map((a) => a.name),
    ].join(' ')
  );
}

function passesEmbed(o: FinderOpportunity, e: EmbedOptions): boolean {
  if (e.ids.length > 0 && !e.ids.includes(o.id)) return false;
  if (e.exclude.includes(o.id)) return false;
  if (e.includeMinistries.length > 0 && !e.includeMinistries.includes(o.ministryId)) return false;
  if (e.excludeMinistries.includes(o.ministryId)) return false;
  if (e.includePrograms.length > 0 && !e.includePrograms.includes(o.programId)) return false;
  if (e.excludePrograms.includes(o.programId)) return false;
  if (e.eventId !== null && o.addToEventId !== e.eventId) return false;
  if (e.attributeIds.length > 0 && !o.attributes.some((a) => e.attributeIds.includes(a.id)))
    return false;
  return true;
}

export function matchOpportunity(
  o: FinderOpportunity,
  f: FilterState,
  e: EmbedOptions,
  hay: string = haystack(o)
): boolean {
  if (!passesEmbed(o, e)) return false;
  if (f.campus !== null && o.congregationId !== f.campus && o.congregationId !== CHURCH_WIDE)
    return false;
  if (f.ministry !== null && o.ministryId !== f.ministry) return false;
  if (f.program !== null && o.programId !== f.program) return false;
  if (f.type !== null && o.typeId !== f.type) return false;
  if (f.frequency === 1 && o.date !== null) return false;
  if (f.frequency === 2 && o.date === null) return false;
  if (f.gender !== null && o.requiredGenderId !== null && o.requiredGenderId !== f.gender)
    return false;
  if (f.age !== null && o.minimumAge !== null && o.minimumAge > f.age) return false;

  const terms = normalize(f.search).split(/\s+/).filter(Boolean);
  if (terms.length > 0 && !terms.every((t) => hay.includes(t))) return false;
  return true;
}

/**
 * Everything that matches, in the catalogue's order (one-time roles soonest
 * first, then ongoing ones newest first), capped by the embed's limit.
 */
export function filterOpportunities(
  opportunities: FinderOpportunity[],
  f: FilterState,
  e: EmbedOptions
): FinderOpportunity[] {
  // A Collection stands in for its children: one "Thanksgiving Boxes" card,
  // not fifteen roles out of context. A child of an unlisted Collection
  // still shows on its own. Same rule as foldCollections in apps/api.
  const listed = new Set(opportunities.map((o) => o.id));
  const hits = opportunities.filter(
    (o) => (o.parentId == null || !listed.has(o.parentId)) && matchOpportunity(o, f, e)
  );
  return e.limit !== null ? hits.slice(0, e.limit) : hits;
}
